import { useCallback, useEffect, useState } from "react";
import { Zap, Download, LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { listLeads, loginStaff, leadsToCsv } from "@/lib/pb";

const TOKEN_KEY = "lpos-admin-token";

const TYPE_LABELS = {
  cafeteria: "Cafetería",
  restaurante_bar: "Restaurante/Bar",
  tienda: "Tienda",
  hotel: "Hotel",
  food_truck: "Puesto/Food truck",
  servicios: "Servicios",
  otro: "Otro",
};

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-[#52525b] focus:border-[#f5a623]/60 transition-colors";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setLeads(null);
  }, []);

  const fetchLeads = useCallback(async (tk) => {
    try {
      setLeads(await listLeads(tk));
    } catch (e) {
      // El token de PocketBase dura poco: 401/403 = sesion vencida, se sale limpio.
      if (e.status === 401 || e.status === 403) logout();
      else toast.error("No se pudieron cargar los leads");
    }
  }, [logout]);

  useEffect(() => {
    if (token) fetchLeads(token);
  }, [token, fetchLeads]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const tk = await loginStaff(email, password);
      localStorage.setItem(TOKEN_KEY, tk);
      setToken(tk);
    } catch (err) {
      setError(
        err.status === 429
          ? "Demasiados intentos. Espera 15 minutos."
          : "Correo o contraseña incorrectos",
      );
    } finally {
      setLoading(false);
    }
  };

  // El CSV se arma en el navegador con lo que ya se trajo: sin backend no hay
  // endpoint de exportacion.
  const exportCsv = () => {
    if (!leads) return;
    const blob = new Blob([leadsToCsv(leads)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "lightning-pos-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#09090b] text-white grid place-items-center px-6">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/[0.02] p-8"
        >
          <div className="flex items-center gap-2 font-black tracking-tighter text-lg mb-6">
            <span className="grid place-items-center w-8 h-8 rounded-lg bg-[#f5a623] text-[#09090b]">
              <Zap size={18} fill="currentColor" />
            </span>
            Panel de leads
          </div>
          <label htmlFor="admin-email" className="block text-sm font-semibold mb-1.5">
            Correo
          </label>
          <input
            id="admin-email"
            data-testid="admin-email-input"
            type="email"
            className={inputCls}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <label htmlFor="admin-password" className="block text-sm font-semibold mb-1.5 mt-4">
            Contraseña
          </label>
          <input
            id="admin-password"
            data-testid="admin-password-input"
            type="password"
            className={inputCls}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {error && (
            <p className="mt-3 text-sm text-red-400" role="alert" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-button"
            disabled={loading}
            className="mt-6 w-full rounded-full bg-[#f5a623] text-[#09090b] font-bold py-3 hover:bg-[#ffbb3d] transition-colors disabled:opacity-60"
          >
            {loading ? "Entrando…" : "Entrar"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] text-white px-4 sm:px-8 py-8">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-2xl font-black tracking-tighter">
            Leads <span className="text-[#f5a623]">({leads ? leads.length : "…"})</span>
          </h1>
          <div className="flex gap-2">
            <button
              onClick={() => fetchLeads(token)}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold hover:border-[#f5a623] transition-colors"
            >
              <RefreshCw size={14} /> Actualizar
            </button>
            <button
              onClick={exportCsv}
              data-testid="admin-export-button"
              className="inline-flex items-center gap-2 rounded-full bg-[#f5a623] text-[#09090b] px-4 py-2 text-sm font-bold hover:bg-[#ffbb3d] transition-colors"
            >
              <Download size={14} /> Exportar CSV
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold hover:border-red-400 hover:text-red-400 transition-colors"
            >
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>

        <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full min-w-[900px] text-sm" data-testid="admin-leads-table">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.03] text-left text-[#a1a1aa]">
                <th className="p-3 font-semibold">Fecha</th>
                <th className="p-3 font-semibold">Negocio</th>
                <th className="p-3 font-semibold">Correo</th>
                <th className="p-3 font-semibold">WhatsApp</th>
                <th className="p-3 font-semibold">Tipo</th>
                <th className="p-3 font-semibold">Ciudad</th>
                <th className="p-3 font-semibold">Terminales</th>
                <th className="p-3 font-semibold">Idioma</th>
                <th className="p-3 font-semibold">UTM</th>
              </tr>
            </thead>
            <tbody>
              {leads && leads.length === 0 && (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-[#71717a]">
                    Todavía no hay leads.
                  </td>
                </tr>
              )}
              {(leads || []).map((l) => (
                <tr key={l.id} className="border-b border-white/5 last:border-b-0">
                  <td className="p-3 text-[#a1a1aa] whitespace-nowrap">
                    {(l.created || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="p-3 font-semibold">{l.business_name}</td>
                  <td className="p-3">{l.email}</td>
                  <td className="p-3 text-[#a1a1aa]">{l.whatsapp || "—"}</td>
                  <td className="p-3 text-[#a1a1aa]">{TYPE_LABELS[l.business_type] || l.business_type}</td>
                  <td className="p-3 text-[#a1a1aa]">{l.city || "—"}</td>
                  <td className="p-3">{l.terminals}</td>
                  <td className="p-3 uppercase text-[#a1a1aa]">{l.language}</td>
                  <td className="p-3 text-[#a1a1aa]">
                    {[l.utm_source, l.utm_medium, l.utm_campaign].filter(Boolean).join(" / ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
